/**
 * @description Uses a client-side pagination workflow (where data is segmented into pages by the UI) to provides state for page,
 *  rowsPerPage, order, orderBy, and total records state as well as callback functions that handle pagination, page size changes,
 *  and sort events.
 */

import React from 'react';

import { ASC, DESC, defaultCompare } from '../../utils/sort-compare';

/**
 * @param {{ [columnId: string]: function }} params.compareMap - a list of sort compare functions mapped to specific columns by id
 * @param {object[]} params.data - table data to be paginated
 * @param {string} params.defaultOrder - the default order direction on page load (ASC or DESC)
 * @param {string} params.defaultOrderBy - the default order by column id on page load
 * @param {number} params.defaultPageSize - the default page size on page load
 * @param {number} params.startingPage - the starting page index on page load
 */
const useClientSidePagination = ({
  compareMap,
  data,
  defaultOrder,
  defaultOrderBy,
  defaultPageSize = 25,
  startingPage = 0
}) => {
  // Keeps track of the number of rows per page to display
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultPageSize);

  // Keeps track of the current page in the table
  const [page, setPage] = React.useState(startingPage);

  // Keeps track of the current sort order ("asc" for ascending or "desc" for descending)
  const [order, setOrder] = React.useState(defaultOrder);

  // Keeps track of the current column id that is being sorted
  const [orderBy, setOrderBy] = React.useState(defaultOrderBy);

  /**
   * Handles page changes by setting the new page value
   * @param {number} newValue - new current page value
   */
  function handlePageChange (newValue) {
    setPage(newValue);
  }

  /**
   * Handles changes in the row per page setting by saving the state and resetting the page back to the first page
   * @param {number} newValue - new rows per page setting
   */
  function handleChangeRowsPerPage (newValue) {
    setRowsPerPage(newValue);
    setPage(0);
  }

  /**
   * Resets the page state back to the first page
   */
  function handleResetPage () {
    setPage(0);
  }

  /**
   * Handles sort changes by setting the sort order and order by state as well as resetting the page back to the
   *  first page
   * @param {string} columnId - column id to be sorted
   */
  function handleSort (columnId) {
    // Determine if the column to be sorted is already sorted in ascending or decending order
    const isAsc = orderBy === columnId && order === ASC;
    const isDesc = orderBy === columnId && order === DESC;

    // If the column is already in ascending, set it to descending
    // If the column is already in descending, clear the sort order
    let newOrder = isAsc ? DESC : ASC;
    newOrder = isDesc ? null : newOrder;

    // If the new sort order is cleared, clear the order by state too
    const newOrderBy = newOrder === null ? null : columnId;

    setOrder(newOrder);
    setOrderBy(newOrderBy);
    setPage(0);
  }

  // Determine the current paginated data set based on the current page, rows per page, and sort settings
  const pageData = React.useMemo(() => {
    // We should be sorting on a copy of the data array, so that way we can revert back to the orginal state if the
    //  sort order is cleared
    let sortedData = data?.slice(0) ?? [];

    // If sort state is set, sort the data set first
    if (order && orderBy) {
      sortedData = sortedData.sort((entryA, entryB) => {
        const valueA = entryA[orderBy];
        const valueB = entryB[orderBy];

        // If the column to be sorted has a specified compare function, use that for sorting. Otherwise fallback to default
        //  sort function
        const fn = compareMap?.[orderBy] ?? defaultCompare;

        return fn(valueA, valueB, order);
      });
    }

    // Return a slice of the sorted data based on the user's current page and page size
    return sortedData.slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage);
  }, [data, order, orderBy, page, rowsPerPage]);

  // Determine the total number of records
  const total = data?.length ?? 0;

  /**
   * Adjusts the current page state by examing changes to the page size and total number of records.
   *
   * This handles the scenario where the current page that the user is viewing may no longer exist because all the records
   *  on the current page have been deleted, due to an action they or another user performed.
   */
  React.useLayoutEffect(() => {
    if (page === 0 && total === 0) {
      // do nothing
    } else if (page * rowsPerPage >= total) {
      const lastPage = Math.ceil(total / rowsPerPage) - 1;

      setPage(lastPage);
    }
  }, [page, rowsPerPage, total]);

  return {
    handleChangeRowsPerPage,
    handlePageChange,
    handleResetPage,
    handleSort,
    order,
    orderBy,
    page,
    pageData,
    rowsPerPage,
    total
  };
}

export default useClientSidePagination;
